import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

const TYPE_LABELS = {
  agent_initial:      { label: 'Initial Outreach',  color: '#8B6F47' },
  agent_followup_3:   { label: 'Day 3 Bump',        color: '#C08B3C' },
  agent_followup_7:   { label: 'Day 7 Final',       color: '#C08B3C' },
  agent_reply:        { label: 'Agent Reply',       color: '#6B6760' },
  agent_counter:      { label: 'Counter Offer',     color: '#8B6F47' },
  buyer_announcement: { label: 'Buyer Blast',       color: '#8B6F47' },
  buyer_followup_2:   { label: 'Buyer Day 2',       color: '#C08B3C' },
  buyer_followup_3:   { label: 'Buyer Day 3 SMS',   color: '#C08B3C' },
};

const STATUS_META = {
  sent:      { label: 'Sent',      color: '#4A7C59', bg: 'rgba(74, 124, 89,0.12)' },
  simulated: { label: 'Simulated', color: '#C08B3C', bg: 'rgba(192, 139, 60,0.12)' },
  failed:    { label: 'Failed',    color: '#B85450', bg: 'rgba(184, 84, 80,0.12)' },
  queued:    { label: 'Queued',    color: '#6B6760', bg: 'rgba(156, 150, 144,0.12)' },
};

function relativeTime(iso) {
  if (!iso) return '';
  const dt = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z');
  const diff = Date.now() - dt.getTime();
  const min = Math.round(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.round(hr / 24)}d ago`;
}

// Top-level audience tabs.
//   acquisitions = you ↔ listing agents (about getting deals)   → buyer_id IS NULL
//   sales        = you ↔ buyers (about assigning deals)         → buyer_id IS NOT NULL
//   all          = everything (no filter)
const AUDIENCE_TABS = [
  { key: 'acquisitions', label: 'Acquisitions', desc: 'Agent conversations', color: '#8B6F47' },
  { key: 'sales',        label: 'Sales',        desc: 'Buyer conversations', color: '#8B6F47' },
  { key: 'all',          label: 'All',          desc: 'Combined view',       color: '#A8B8D8' },
];

// Buyer-status filter pills (only visible on Sales audience). Mirrors BuyerStatusBadge's enum
// so adding/renaming a status only requires updating that one file.
const BUYER_STATUS_FILTERS = [
  { key: 'all',          label: 'All buyers',  color: 'var(--gray-300)' },
  { key: 'new',          label: 'New',          color: '#C08B3C' },
  { key: 'working',      label: 'Working',      color: '#B89968' },
  { key: 'onboarded',    label: 'Onboarded',    color: '#4A7C59' },
  { key: 'buyer',        label: 'Buyer',        color: '#8B6F47' },
  { key: 'unqualified',  label: 'Unqualified',  color: '#8A7969' },
  { key: 'blacklisted',  label: 'Blacklisted',  color: '#B85450' },
  { key: 'unsubscribed', label: 'Unsubscribed', color: '#6B6760' },
];

export default function Communications() {
  const navigate = useNavigate();
  const [comms, setComms]       = useState([]);
  const [counts, setCounts]     = useState({ acquisitions: 0, sales: 0, total: 0 });
  const [buyerStatusCounts, setBuyerStatusCounts] = useState({}); // { onboarded: 12, buyer: 4, ... }
  const [loading, setLoading]   = useState(true);
  const [audience, setAudience] = useState('acquisitions'); // acquisitions | sales | all
  const [channel, setChannel]   = useState('all');     // all | email | sms
  const [status, setStatus]     = useState('all');     // all | sent | simulated | failed
  const [direction, setDirection] = useState('all');   // all | outbound | inbound
  const [buyerStatus, setBuyerStatus] = useState('all'); // 'all' | one of BUYER_STATUSES
  const [search, setSearch]     = useState('');
  const [expanded, setExpanded] = useState({});
  const [replyOpen, setReplyOpen] = useState(null);   // communication id
  const [replyBody, setReplyBody] = useState('');
  const [replySending, setReplySending] = useState(false);

  async function sendReply(commId) {
    if (!replyBody.trim()) return;
    setReplySending(true);
    try {
      const r = await fetch(`/api/communications/${commId}/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body: replyBody }),
      });
      const data = await r.json();
      if (data.error) throw new Error(data.error);
      setReplyOpen(null);
      setReplyBody('');
      load();
    } catch (err) {
      alert('Reply failed: ' + err.message);
    }
    setReplySending(false);
  }

  function load() {
    setLoading(true);
    const params = new URLSearchParams({ limit: '500' });
    if (audience !== 'all') params.append('audience', audience);
    // Buyer-status filter only meaningful for the Sales audience (acquisition rows have no buyer).
    if (audience === 'sales' && buyerStatus !== 'all') params.append('buyer_status', buyerStatus);
    fetch(`/api/communications?${params}`)
      .then(r => r.json())
      .then(data => {
        // Backend returns { communications, counts, buyer_status_counts }. Fall through
        // to bare array for backward compat if someone reverts the endpoint shape.
        if (Array.isArray(data)) {
          setComms(data);
          setCounts({ acquisitions: 0, sales: 0, total: data.length });
          setBuyerStatusCounts({});
        } else {
          setComms(data.communications || []);
          setCounts(data.counts || { acquisitions: 0, sales: 0, total: 0 });
          // Reshape array → { status: n } for easy lookup by pill key.
          const bsc = {};
          for (const r of (data.buyer_status_counts || [])) bsc[r.status] = r.n;
          setBuyerStatusCounts(bsc);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }

  // Reload whenever the audience tab or buyer-status filter flips — both live server-side.
  useEffect(load, [audience, buyerStatus]);

  // Reset the buyer-status filter when leaving the Sales tab (it doesn't apply elsewhere).
  useEffect(() => { if (audience !== 'sales' && buyerStatus !== 'all') setBuyerStatus('all'); }, [audience, buyerStatus]);

  // Stats — only count OUTBOUND messages in sent/simulated/failed; inbound counted separately as replies
  const stats = useMemo(() => {
    const s = { total: comms.length, sent: 0, simulated: 0, failed: 0, opened: 0, replied: 0, email: 0, sms: 0 };
    for (const c of comms) {
      if (c.direction === 'inbound') {
        s.replied++;
      } else {
        if (c.status === 'sent') s.sent++;
        else if (c.status === 'simulated') s.simulated++;
        else if (c.status === 'failed') s.failed++;
      }
      if (c.opened_at) s.opened++;
      if (c.channel === 'email') s.email++;
      if (c.channel === 'sms') s.sms++;
    }
    return s;
  }, [comms]);

  const filtered = useMemo(() => {
    return comms.filter(c => {
      if (channel !== 'all' && c.channel !== channel) return false;
      if (status !== 'all' && c.status !== status) return false;
      if (direction !== 'all' && c.direction !== direction) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        if (!(c.recipient || '').toLowerCase().includes(q) &&
            !(c.subject || '').toLowerCase().includes(q) &&
            !(c.body || '').toLowerCase().includes(q) &&
            !(c.address || '').toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [comms, channel, status, direction, search]);

  // Detect simulation mode
  const simulationMode = stats.simulated > 0 && stats.sent === 0;

  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--white)', letterSpacing: '-0.02em', marginBottom: 4 }}>Communications</h1>
        <p style={{ color: 'var(--gray-500)', fontSize: 13 }}>All emails and SMS sent from DealFlow AI · sorted newest first</p>
      </div>

      {/* Audience tabs — primary grouping. Acquisitions = agent threads, Sales = buyer threads. */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {AUDIENCE_TABS.map(t => {
          const active = audience === t.key;
          const n = t.key === 'all' ? counts.total : counts[t.key];
          return (
            <button
              key={t.key}
              onClick={() => setAudience(t.key)}
              style={{
                flex: 1, textAlign: 'left',
                padding: '12px 16px', borderRadius: 12,
                background: active ? `${t.color}15` : 'var(--navy-card)',
                border: `1px solid ${active ? t.color : 'var(--navy-border)'}`,
                cursor: 'pointer', transition: 'all 0.15s',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: active ? t.color : 'var(--white)' }}>{t.label}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: active ? t.color : 'var(--gray-500)' }}>· {n.toLocaleString()}</span>
              </div>
              <div style={{ fontSize: 11, color: active ? 'var(--gray-300)' : 'var(--gray-500)', marginTop: 2 }}>{t.desc}</div>
            </button>
          );
        })}
      </div>

      {simulationMode && (
        <div style={{
          marginBottom: 20, padding: '14px 18px',
          background: 'rgba(192, 139, 60,0.08)', border: '1px solid rgba(192, 139, 60,0.35)', borderRadius: 10,
          display: 'flex', alignItems: 'flex-start', gap: 12,
        }}>
          <div style={{ fontSize: 18 }}>⚠</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--gold)', marginBottom: 4 }}>Simulation mode — no real emails or SMS are being sent</div>
            <div style={{ fontSize: 12, color: 'var(--gray-300)', lineHeight: 1.55 }}>
              Your <code style={{ background: 'rgba(0,0,0,0.3)', padding: '1px 6px', borderRadius: 4 }}>RESEND_API_KEY</code> and <code style={{ background: 'rgba(0,0,0,0.3)', padding: '1px 6px', borderRadius: 4 }}>TWILIO_*</code> credentials are still placeholders in <code style={{ background: 'rgba(0,0,0,0.3)', padding: '1px 6px', borderRadius: 4 }}>.env</code>. Everything below is logged but not actually delivered. To send for real: get a Resend API key, verify <strong>fourthphaserealty.com</strong> in their dashboard, paste the key into <code style={{ background: 'rgba(0,0,0,0.3)', padding: '1px 6px', borderRadius: 4 }}>RESEND_API_KEY=</code>, and restart the backend.
            </div>
          </div>
        </div>
      )}

      {/* Stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 12, marginBottom: 24 }}>
        <StatBox label="Total"          value={stats.total} />
        <StatBox label="Sent (real)"    value={stats.sent}      color="#4A7C59" hint="Delivered via Resend/Twilio" />
        <StatBox label="Simulated"      value={stats.simulated} color="#C08B3C" hint="Logged only — no API key set" />
        <StatBox label="Failed"         value={stats.failed}    color="#B85450" hint="Send attempt errored" />
        <StatBox label="Opened"         value={stats.opened}    color="#6B6760" hint="Recipient opened email" />
        <StatBox label="Manually Logged" value={stats.replied}  color="#8B6F47" hint='You pasted in a reply' />
      </div>

      {/* Buyer-status filter — only visible on Sales audience. Sticks above the main
          filter bar as its own row so the count badges per status read at a glance. */}
      {audience === 'sales' && (
        <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12, padding: '12px 16px', marginBottom: 8, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginRight: 4 }}>
            Filter by buyer status
          </div>
          {BUYER_STATUS_FILTERS.map(f => {
            const active = buyerStatus === f.key;
            // For 'all' the count is just the running sales total; otherwise pull the per-status count
            // from the backend's buyer_status_counts breakdown.
            const n = f.key === 'all' ? counts.sales : (buyerStatusCounts[f.key] || 0);
            return (
              <button
                key={f.key}
                onClick={() => setBuyerStatus(f.key)}
                style={{
                  padding: '5px 11px', borderRadius: 999,
                  border: `1px solid ${active ? f.color : 'var(--navy-border)'}`,
                  background: active ? `${f.color}1F` : 'transparent',
                  color: active ? f.color : 'var(--gray-500)',
                  fontSize: 11, fontWeight: 700, cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                }}
              >
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: f.color, opacity: active ? 1 : 0.6 }} />
                {f.label}
                <span style={{ fontSize: 10, opacity: 0.7 }}>· {(n || 0).toLocaleString()}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Filter bar */}
      <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12, padding: '14px 16px', marginBottom: 14, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <PillGroup value={channel} setValue={setChannel} options={[
          { key: 'all', label: `All (${stats.total})` },
          { key: 'email', label: `Email (${stats.email})` },
          { key: 'sms', label: `SMS (${stats.sms})` },
        ]} />
        <div style={{ width: 1, height: 22, background: 'var(--navy-border)' }} />
        <PillGroup value={direction} setValue={setDirection} options={[
          { key: 'all', label: 'In + Out' },
          { key: 'outbound', label: 'Outbound' },
          { key: 'inbound', label: 'Inbound' },
        ]} />
        <div style={{ width: 1, height: 22, background: 'var(--navy-border)' }} />
        <PillGroup value={status} setValue={setStatus} options={[
          { key: 'all', label: 'Any status' },
          { key: 'sent', label: 'Sent', color: '#4A7C59' },
          { key: 'simulated', label: 'Simulated', color: '#C08B3C' },
          { key: 'failed', label: 'Failed', color: '#B85450' },
        ]} />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search recipient, subject, body, address…"
          style={{ marginLeft: 'auto', padding: '6px 12px', borderRadius: 8, border: '1px solid var(--navy-border)', background: 'var(--navy)', color: 'var(--white)', fontSize: 12, width: 240, outline: 'none' }}
        />
        <button onClick={load} title="Refresh" style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid var(--navy-border)', background: 'var(--navy-card)', color: 'var(--gray-300)', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>↻</button>
      </div>

      {/* Table */}
      <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-500)' }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: 'var(--gray-500)', fontSize: 13 }}>
            {comms.length === 0
              ? (audience === 'acquisitions'
                  ? 'No agent conversations yet. Send an outreach email from any deal to start one.'
                  : audience === 'sales'
                    ? <>No buyer conversations yet. Market a deal under contract from the <span onClick={() => navigate('/pipeline')} style={{ color: 'var(--teal)', cursor: 'pointer', textDecoration: 'underline' }}>Pipeline</span> to fire your first blast.</>
                    : 'No communications yet.')
              : 'No communications match these filters.'}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1000 }}>
              <thead>
                <tr style={{ background: 'rgba(250, 247, 242,0.6)', borderBottom: '1px solid var(--navy-border)' }}>
                  {['', 'Type', 'Status', 'To / From', 'Subject', 'Property', 'When'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', fontSize: 10, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', textAlign: 'left', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(c => {
                  const isOpen = expanded[c.id];
                  const tm = TYPE_LABELS[c.type] || { label: c.type || '—', color: '#6B6760' };
                  const sm = STATUS_META[c.status] || { label: c.status, color: 'var(--gray-300)', bg: 'rgba(156, 150, 144,0.1)' };
                  const isInbound = c.direction === 'inbound';
                  return (
                    <React.Fragment key={c.id}>
                      <tr
                        onClick={() => setExpanded(e => ({ ...e, [c.id]: !isOpen }))}
                        style={{ borderBottom: '1px solid rgba(232, 227, 218, 0.7)', cursor: 'pointer' }}
                        onMouseOver={e => e.currentTarget.style.background = 'rgba(139, 111, 71,0.04)'}
                        onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <td style={{ padding: '10px 14px', width: 22 }}>
                          <span style={{ fontSize: 14, color: c.channel === 'email' ? 'var(--teal)' : '#8B6F47' }}>
                            {c.channel === 'email' ? '✉' : '📱'}
                          </span>
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 6, background: `${tm.color}15`, color: tm.color, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase' }}>{tm.label}</span>
                            {isInbound && <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 6, background: 'rgba(107, 103, 96,0.15)', color: '#6B6760', fontWeight: 700 }}>IN</span>}
                          </div>
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          {isInbound ? (
                            <span style={{ fontSize: 10, padding: '3px 9px', borderRadius: 8, background: 'rgba(107, 103, 96,0.15)', color: '#6B6760', fontWeight: 700, whiteSpace: 'nowrap' }}>Manually Logged</span>
                          ) : (
                            <span style={{ fontSize: 10, padding: '3px 9px', borderRadius: 8, background: sm.bg, color: sm.color, fontWeight: 700, whiteSpace: 'nowrap' }}>{sm.label}</span>
                          )}
                          {c.opened_at && <span style={{ marginLeft: 4, fontSize: 9, padding: '2px 6px', borderRadius: 4, background: 'rgba(107, 103, 96,0.15)', color: '#6B6760', fontWeight: 600 }}>OPENED</span>}
                        </td>
                        <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--gray-300)', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={isInbound ? c.sender : c.recipient}>
                          {isInbound ? c.sender : c.recipient}
                        </td>
                        <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--white)', fontWeight: 500, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {c.subject || <span style={{ color: 'var(--gray-500)', fontStyle: 'italic' }}>(no subject)</span>}
                        </td>
                        <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--gray-300)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {c.deal_id && c.address ? (
                            <span onClick={(e) => { e.stopPropagation(); navigate(`/underwriting/${c.deal_id}`); }}
                                  style={{ color: 'var(--teal)', cursor: 'pointer', textDecoration: 'underline' }}>
                              {c.address}
                            </span>
                          ) : (c.buyer_name || '—')}
                        </td>
                        <td style={{ padding: '10px 14px', fontSize: 11, color: 'var(--gray-500)', whiteSpace: 'nowrap' }}>{relativeTime(c.sent_at)}</td>
                      </tr>
                      {isOpen && (
                        <tr>
                          <td colSpan={7} style={{ background: 'rgba(250, 247, 242,0.4)', padding: '14px 22px', borderBottom: '1px solid var(--navy-border)' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 12, fontSize: 11 }}>
                              <Field label="From" value={c.sender} />
                              <Field label="To"   value={c.recipient} />
                              <Field label="Status" value={`${c.status}${c.error ? ' — ' + c.error : ''}`} color={sm.color} />
                              <Field label="Message ID" value={c.message_id || '—'} />
                              <Field label="Sent" value={c.sent_at} />
                              {c.opened_at && <Field label="Opened" value={c.opened_at} color="#6B6760" />}
                              {c.replied_at && <Field label="Replied" value={c.replied_at} color="#8B6F47" />}
                            </div>
                            {c.subject && <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--white)', marginBottom: 8 }}>{c.subject}</div>}
                            {c.body && (
                              <pre style={{ fontSize: 12, color: 'var(--gray-300)', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0, fontFamily: 'inherit', maxHeight: 320, overflowY: 'auto', background: 'var(--navy)', borderRadius: 6, padding: 12, border: '1px solid var(--navy-border)' }}>{c.body}</pre>
                            )}
                            {/* Reply UI for inbound messages */}
                            {c.direction === 'inbound' && (
                              <div style={{ marginTop: 12 }}>
                                {replyOpen === c.id ? (
                                  <div style={{ background: 'var(--navy)', border: '1px solid var(--teal)', borderRadius: 8, padding: 10 }}>
                                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--teal)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Reply to {c.sender}</div>
                                    <textarea
                                      value={replyBody}
                                      onChange={e => setReplyBody(e.target.value)}
                                      placeholder="Type your reply..."
                                      rows={5}
                                      style={{ width: '100%', padding: 10, fontSize: 12, lineHeight: 1.5, background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 6, color: 'var(--white)', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical' }}
                                    />
                                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                      <button onClick={() => { setReplyOpen(null); setReplyBody(''); }} style={{ padding: '6px 14px', borderRadius: 6, background: 'transparent', border: '1px solid var(--navy-border)', color: 'var(--gray-500)', fontSize: 11, cursor: 'pointer' }}>Cancel</button>
                                      <button onClick={() => sendReply(c.id)} disabled={replySending || !replyBody.trim()} style={{ padding: '6px 14px', borderRadius: 6, background: 'var(--teal)', border: 'none', color: '#fff', fontSize: 11, fontWeight: 700, cursor: replyBody.trim() ? 'pointer' : 'not-allowed', opacity: replyBody.trim() ? 1 : 0.5 }}>
                                        {replySending ? 'Sending…' : 'Send Reply'}
                                      </button>
                                      {c.deal_id && (
                                        <button onClick={() => navigate(`/underwriting/${c.deal_id}`)} style={{ padding: '6px 14px', borderRadius: 6, background: 'rgba(139, 111, 71,0.15)', border: '1px solid rgba(139, 111, 71,0.35)', color: '#8B6F47', fontSize: 11, fontWeight: 700, cursor: 'pointer', marginLeft: 'auto' }}>
                                          Open AI Negotiation →
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  <button onClick={(e) => { e.stopPropagation(); setReplyOpen(c.id); }}
                                    style={{ padding: '6px 14px', borderRadius: 6, background: 'rgba(139, 111, 71,0.12)', border: '1px solid rgba(139, 111, 71,0.4)', color: 'var(--teal)', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                                    ✎ Reply in-app
                                  </button>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatBox({ label, value, color = 'var(--white)', hint }) {
  return (
    <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 10, padding: '14px 16px' }} title={hint || ''}>
      <div style={{ fontSize: 22, fontWeight: 700, color }}>{value || 0}</div>
      <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 2, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      {hint && <div style={{ fontSize: 9, color: 'var(--gray-500)', marginTop: 2, opacity: 0.7 }}>{hint}</div>}
    </div>
  );
}

function PillGroup({ value, setValue, options }) {
  return (
    <div style={{ display: 'flex', gap: 4 }}>
      {options.map(opt => {
        const active = value === opt.key;
        const c = opt.color || 'var(--teal)';
        return (
          <button key={opt.key} onClick={() => setValue(opt.key)} style={{
            padding: '5px 11px', borderRadius: 14, border: 'none', cursor: 'pointer',
            fontSize: 11, fontWeight: 600,
            background: active ? c : 'transparent',
            color: active ? '#fff' : 'var(--gray-500)',
          }}>{opt.label}</button>
        );
      })}
    </div>
  );
}

function Field({ label, value, color = 'var(--gray-300)' }) {
  return (
    <div>
      <div style={{ fontSize: 9, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 12, color, wordBreak: 'break-all' }}>{value || '—'}</div>
    </div>
  );
}
